import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Loader2, MessageSquare, Send, Building2, UserRound, ArrowLeft } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Messages = () => {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  const loadConvos = useCallback(() => {
    if (!user) return;
    api.get("/conversations").then((r) => setConvos(r.data)).finally(() => setLoadingList(false));
  }, [user]);
  useEffect(() => { loadConvos(); }, [loadConvos]);

  const loadThread = useCallback((cid, showSpinner = true) => {
    if (!cid) return;
    if (showSpinner) setLoadingThread(true);
    api.get(`/conversations/${cid}/messages`)
      .then((r) => setThread(r.data))
      .catch(() => setThread(null))
      .finally(() => setLoadingThread(false));
  }, []);

  useEffect(() => {
    if (id) loadThread(id);
    else setThread(null);
  }, [id, loadThread]);

  // light polling for new messages on open thread
  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => loadThread(id, false), 5000);
    return () => clearInterval(t);
  }, [id, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !id) return;
    setSending(true);
    try {
      await api.post(`/conversations/${id}/messages`, { text });
      setText("");
      loadThread(id, false);
      loadConvos();
    } finally {
      setSending(false);
    }
  };

  if (authLoading || !user)
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  const other = thread?.other;

  return (
    <PageShell plain>
      <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-[#1E4FA3]" />
          <h1 className="font-heading text-[28px] font-extrabold text-[#0B2545] sm:text-[34px]">Messages</h1>
        </div>

        <div className="mt-5 grid h-[70vh] grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
          {/* Conversation list */}
          <div className={`overflow-y-auto rounded-2xl border border-slate-100 bg-white shadow-sm ${id ? "hidden md:block" : "block"}`}>
            {loadingList ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#1E4FA3]" /></div>
            ) : convos.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No conversations yet. Start one from your dashboard.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {convos.map((c) => {
                  const active = c.id === id;
                  return (
                    <li key={c.id}>
                      <button onClick={() => navigate(`/messages/${c.id}`)} className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${active ? "bg-[#1E4FA3]/5" : ""}`}>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1E4FA3]/10 font-heading text-xs font-bold text-[#1E4FA3]">{initials(c.other?.name)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-[#0B2545]">{c.other?.name}</p>
                          <p className="truncate text-xs text-slate-500">{c.last_message || (c.other?.title || (c.other?.role === "organisation" ? "Organisation" : "Professional"))}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Thread */}
          <div className={`flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm ${id ? "flex" : "hidden md:flex"}`}>
            {!id ? (
              <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-slate-400">
                <MessageSquare className="h-10 w-10" />
                <p className="mt-3 text-sm">Select a conversation to start messaging.</p>
              </div>
            ) : loadingThread && !thread ? (
              <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div>
            ) : thread ? (
              <>
                {/* header */}
                <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
                  <button onClick={() => navigate("/messages")} className="md:hidden"><ArrowLeft className="h-5 w-5 text-slate-500" /></button>
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E4FA3]/10 font-heading text-xs font-bold text-[#1E4FA3]">{initials(other?.name)}</div>
                  <div>
                    <p className="font-semibold text-[#0B2545]">{other?.name}</p>
                    <p className="flex items-center gap-1 text-xs text-slate-500">
                      {other?.role === "organisation" ? <Building2 className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
                      {other?.title || (other?.role === "organisation" ? "Organisation" : "Professional")}
                    </p>
                  </div>
                </div>

                {/* messages */}
                <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4">
                  {thread.messages.length === 0 && (
                    <p className="mt-6 text-center text-sm text-slate-400">No messages yet. Say hello!</p>
                  )}
                  {thread.messages.map((m) => {
                    const mine = m.sender_user_id === thread.me;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed ${mine ? "rounded-br-md bg-[#1E4FA3] text-white" : "rounded-bl-md bg-white text-slate-700 ring-1 ring-slate-100"}`}>
                          <p>{m.text}</p>
                          <p className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* composer */}
                <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-100 p-3">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." className="h-11 flex-1" />
                  <Button type="submit" disabled={sending || !text.trim()} className="h-11 w-11 shrink-0 rounded-xl bg-[#1E4FA3] p-0 text-white hover:bg-[#193f83]">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Conversation not found.</div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default Messages;
